import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { fetchProducts } from "../../lib/api";
import { formatINR } from "../../lib/constants";
import { useCart } from "../../context/CartContext";
import { ButtonRing, useBusyAction } from "../LoadingRing";

// Product card inside an article. Price, offer and stock always come live from the product CRM.
export default function BlogProductCard({ slug, onTrack, compact = false }) {
    const { addItem, setOpen } = useCart();
    const [adding, runAdd] = useBusyAction();
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts, staleTime: 60000 });
    const product = products.find((p) => p.slug === slug);
    if (!product) return null;

    const comingSoon = product.coming_soon || product.purchasable === false;
    const image = (product.images || [])[0];

    const add = () => runAdd(() => {
        addItem(product, 1);
        setOpen(true);
        onTrack?.("product_click", `add_to_cart:${product.slug}`);
    });

    return (
        <div data-testid={`blog-product-card-${product.slug}`}
            className={`not-prose border border-white/10 bg-bg1 hover:border-gold/40 transition-colors ${
                compact ? "flex flex-col gap-3 p-3" : "my-8 flex flex-col sm:flex-row gap-4 p-4"}`}>
            <Link to={`/product/${product.slug}`} onClick={() => onTrack?.("product_click", `view:${product.slug}`)}
                className={`shrink-0 bg-bg0 overflow-hidden ${compact ? "w-full h-32" : "w-full sm:w-32 h-40 sm:h-32"}`}>
                {image ? <img src={image} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-lime/20 to-bg2" />}
            </Link>
            <div className="flex-1 min-w-0">
                <div className="mono text-[9px] tracking-[0.2em] text-gold">REHEALTHME PRODUCT</div>
                <h4 className={`font-display uppercase tracking-tight mt-1 ${compact ? "text-lg" : "text-xl"}`}>{product.name}</h4>
                <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{product.tagline || product.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    {!comingSoon && (
                        <>
                            <span className="font-display text-lg text-white">{formatINR(product.price)}</span>
                            {product.compare_at_price > product.price && (
                                <span className="text-sm text-zinc-500 line-through">{formatINR(product.compare_at_price)}</span>
                            )}
                        </>
                    )}
                    {comingSoon && <span className="mono text-[10px] tracking-[0.16em] text-gold">COMING SOON</span>}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <Link to={`/product/${product.slug}`} data-testid={`blog-product-view-${product.slug}`}
                        onClick={() => onTrack?.("product_click", `view:${product.slug}`)}
                        className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 mono text-[10px]
                                   tracking-[0.16em] hover:border-gold hover:text-gold transition-colors">
                        EXPLORE PRODUCT <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    {comingSoon ? (
                        <span className="inline-flex items-center px-4 py-2 mono text-[10px] tracking-[0.16em] bg-white/5 text-zinc-400">
                            COMING SOON
                        </span>
                    ) : (
                        <button onClick={add} disabled={adding} data-testid={`blog-product-add-${product.slug}`}
                            className="inline-flex items-center gap-2 btn-gold px-4 py-2 mono text-[10px]
                                       tracking-[0.16em] font-bold hover:brightness-110 transition-all">
                            {adding ? <ButtonRing label="ADDING…" />
                                : <><ShoppingBag className="w-3.5 h-3.5" /> ADD TO CART</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
